import { useId, type FC } from "react";
import { useAtom, type PrimitiveAtom } from "jotai";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";
import type { WorkflowEditFormLimitsData } from "@/components/automations/automation-edit-form-data";

type WorkflowEditActionProps = {
    limitsAtom: PrimitiveAtom<WorkflowEditFormLimitsData | undefined>;
};

export const WorkflowEditAction: FC<WorkflowEditActionProps> = ({ limitsAtom }) => {
    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-start gap-4">
                    <div className="min-w-[200px] flex-1">
                        <MaxParallelInput limitsAtom={limitsAtom} />
                    </div>
                    <div className="min-w-[200px] flex-1">
                        <MaxTotalInput limitsAtom={limitsAtom} />
                    </div>
                </div>
                <div className="text-sm text-content-secondary">
                    Note: These limits are currently restricted during beta to ensure system stability. Higher limits
                    will be available after the beta period.
                </div>
            </div>
        </div>
    );
};

type MaxParallelInputProps = {
    limitsAtom: PrimitiveAtom<WorkflowEditFormLimitsData | undefined>;
};

const MaxParallelInput: FC<MaxParallelInputProps> = ({ limitsAtom }) => {
    const id = useId();
    const [limits, setLimits] = useAtom(limitsAtom);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const maxParallel = value === "" ? undefined : parseInt(value, 10);
        setLimits({ ...limits, maxParallel });
    };

    return (
        <InputField
            label="Max concurrent actions"
            hint="The number of actions that can run in parallel per execution"
            id={id}
        >
            <Input
                id={id}
                type="number"
                min={1}
                max={25}
                value={limits?.maxParallel ?? ""}
                onChange={handleChange}
                placeholder={String(DEFAULT_WORKFLOW_LIMITS.maxParallel)}
                className="max-w-none bg-transparent"
                data-testid="max-parallel-input"
            />
        </InputField>
    );
};

type MaxTotalInputProps = {
    limitsAtom: PrimitiveAtom<WorkflowEditFormLimitsData | undefined>;
};

const MaxTotalInput: FC<MaxTotalInputProps> = ({ limitsAtom }) => {
    const id = useId();
    const [limits, setLimits] = useAtom(limitsAtom);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const maxTotal = value === "" ? undefined : parseInt(value, 10);
        setLimits({ ...limits, maxTotal });
    };

    return (
        <InputField
            label="Max total actions"
            hint="The total number of actions that can be created by a single execution"
            id={id}
        >
            <Input
                id={id}
                type="number"
                min={1}
                max={100}
                value={limits?.maxTotal ?? ""}
                onChange={handleChange}
                placeholder={String(DEFAULT_WORKFLOW_LIMITS.maxTotal)}
                className="max-w-none bg-transparent"
                data-testid="max-total-input"
            />
        </InputField>
    );
};
